import { Injectable, signal } from '@angular/core';
import { InMemoryCacheService } from '../cache/in-memory-cache.service';
import { CardRole, ProjectRole } from '../permissions/permission.types';
import { OrganizationBusinessCardsService } from 'src/app/pages/organization-settings/services/organization-business-cards.service';
import { OrganizationProjectsService } from 'src/app/pages/organization-settings/services/organization-projects.service';

@Injectable({
  providedIn: 'root'
})
/**
 * Keeps cached role lookups for projects and cards so permission checks can be fast and synchronous.
 */
export class AccessCacheService {
  private readonly _projectRolesById = new Map<string, ProjectRole>();
  private readonly _cardRolesById = new Map<string, CardRole>();
  private readonly _loadedProjectIds = new Set<string>();
  private readonly _loadedCardIds = new Set<string>();
  private readonly _version = signal(0);
  public readonly version = this._version.asReadonly();

  constructor(
    private readonly cache: InMemoryCacheService,
    private readonly orgProjectsService: OrganizationProjectsService,
    private readonly orgBusinessCardsService: OrganizationBusinessCardsService
  ) {}

  /** Returns the cached role for a project, or null when no membership is known. */
  public getProjectRole(projectId: string): ProjectRole | null {
    return this._projectRolesById.get(projectId) ?? null;
  }

  /** Cache a project role and bump the version so consumers can re-evaluate. */
  public setProjectRole(projectId: string, role: ProjectRole): void {
    this._projectRolesById.set(projectId, role);
    this._loadedProjectIds.add(projectId);
    this.bumpVersion();
  }

  /** Returns the cached role for a card, or null when unknown. */
  public getCardRole(cardId: string): CardRole | null {
    return this._cardRolesById.get(cardId) ?? null;
  }

  /** Cache a card role and notify observers by bumping the version. */
  public setCardRole(cardId: string, role: CardRole): void {
    this._cardRolesById.set(cardId, role);
    this._loadedCardIds.add(cardId);
    this.bumpVersion();
  }

  /** Ensure project memberships for the requested ids are loaded into the cache. */
  public async primeProjects(projectIds: string[]): Promise<void> {
    const userId = this.cache.userId;
    if (!userId || !projectIds?.length) return;

    const idsToLoad = [...new Set(projectIds.filter(
      id => id && !this._loadedProjectIds.has(id) && !this._projectRolesById.has(id)
    ))];
    if (!idsToLoad.length) return;

    const memberships = await this.orgProjectsService.fetchProjectMembershipsByUserId(userId);
    if (memberships === null) return;

    memberships.forEach(membership => {
      if (membership.projectId && membership.role) {
        this._projectRolesById.set(membership.projectId, membership.role as ProjectRole);
      }
    });
    idsToLoad.forEach(projectId => this._loadedProjectIds.add(projectId));
    this.bumpVersion();
  }

  /** Ensure card permissions for the requested ids are loaded into the cache. */
  public async primeCards(cardIds: string[]): Promise<void> {
    const userId = this.cache.userId;
    if (!userId || !cardIds?.length) return;

    const idsToLoad = [...new Set(cardIds.filter(
      id => id && !this._loadedCardIds.has(id) && !this._cardRolesById.has(id)
    ))];
    if (!idsToLoad.length) return;

    const permissions = await this.orgBusinessCardsService.fetchCardPermissionsByUserId(userId);
    if (permissions === null) return;

    permissions.forEach(permission => {
      if (permission.cardId && permission.role) {
        this._cardRolesById.set(permission.cardId, permission.role as CardRole);
      }
    });
    idsToLoad.forEach(cardId => this._loadedCardIds.add(cardId));
    this.bumpVersion();
  }

  /** Clears cached project and card roles when the organization context changes. */
  public clearOnOrgSwitch(): void {
    this._projectRolesById.clear();
    this._cardRolesById.clear();
    this._loadedProjectIds.clear();
    this._loadedCardIds.clear();
    this.bumpVersion();
  }

  private bumpVersion(): void {
    this._version.update(value => value + 1);
  }

  public get projectRolesById(): Map<string, ProjectRole> {
    return this._projectRolesById;
  }

  public get cardRolesById(): Map<string, CardRole> {
    return this._cardRolesById;
  }
}
